import { memo, useCallback, useMemo } from 'react';
import Button from '../Button.jsx';
import Drawer from '../Drawer.jsx';
import FilterSlideoutSectionRenderer from './FilterSlideoutSectionRenderer.jsx';
import {
  FILTER_SLIDEOUT_BODY,
  FILTER_SLIDEOUT_BODY_INNER,
  FILTER_SLIDEOUT_CLOSE_BUTTON,
  FILTER_SLIDEOUT_FOOTER,
  FILTER_SLIDEOUT_HEADER,
  FILTER_SLIDEOUT_LAYOUT,
  FILTER_SLIDEOUT_PANEL,
  FILTER_SLIDEOUT_WIDTH,
} from './filterSlideoutTokens.js';
import {
  clearAllSections,
  hasInvalidSections,
  joinClasses,
} from './filterSlideout.utils.js';
import { X } from 'lucide-react';

const defaultI18n = {
  title: 'All Filters',
  close: 'Close filters',
  clearAll: 'Clear all',
  apply: 'Apply filters',
  empty: 'No filters available',
};

/**
 * Figma-aligned filters slideout with fixed header/footer and composable sections.
 *
 * Pass filter sections via the `sections` prop. Use helpers from
 * `filterSlideoutSectionBuilders` (e.g. `createAssignmentSection`) to build typed configs.
 *
 * @param {import('./FilterSlideout.types.js').FilterSlideoutProps} props
 */
const FilterSlideout = ({
  isOpen,
  onClose,
  size = 'm',
  title,
  sections = [],
  onClearAll,
  onApply,
  applyDisabled = false,
  portalTarget,
  className = '',
  bodyClassName = '',
  i18n: i18nProp,
}) => {
  const i18n = useMemo(() => ({ ...defaultI18n, ...i18nProp }), [i18nProp]);
  const resolvedTitle = title ?? i18n.title;
  const widthClassName = FILTER_SLIDEOUT_WIDTH[size] ?? FILTER_SLIDEOUT_WIDTH.m;

  const invalid = hasInvalidSections(sections);
  const resolvedApplyDisabled = applyDisabled || invalid;

  const handleClearAll = useCallback(() => {
    clearAllSections(sections);
    onClearAll?.();
  }, [sections, onClearAll]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      portalTarget={portalTarget}
      position="right"
      widthClassName={widthClassName}
      // Height is Drawer's to resolve: `h-screen` on a body portal, `h-full`
      // inside an app overlay root. Hardcoding `h-screen` here made the panel
      // taller than the content column it was portaled into.
      lightBgClassName="bg-surface"
      darkBgClassName=""
      backdropClassName="!bg-black/10"
      panelClassName={joinClasses(FILTER_SLIDEOUT_PANEL, className)}
      ariaLabel={resolvedTitle}
    >
      <div className={FILTER_SLIDEOUT_LAYOUT}>
        <header className={FILTER_SLIDEOUT_HEADER}>
          <h2>{resolvedTitle}</h2>
          <button
            type="button"
            className={FILTER_SLIDEOUT_CLOSE_BUTTON}
            onClick={onClose}
            aria-label={i18n.close}
          >
            <X size={24} aria-hidden />
          </button>
        </header>

        <div className={joinClasses(FILTER_SLIDEOUT_BODY, bodyClassName)}>
          <div className={FILTER_SLIDEOUT_BODY_INNER}>
            {sections.length ? (
              sections.map((section) => (
                <FilterSlideoutSectionRenderer key={section.id} section={section} />
              ))
            ) : (
              <p className="text-sm font-medium leading-5 text-content-muted">
                {i18n.empty}
              </p>
            )}
          </div>
        </div>

        <footer className={FILTER_SLIDEOUT_FOOTER}>
          <button
            type="button"
            className="text-sm font-medium leading-5 text-content-secondary underline underline-offset-2 transition-colors hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
            onClick={handleClearAll}
          >
            {i18n.clearAll}
          </button>
          <Button
            type="button"
            variant="classicPrimary"
            size="md"
            disabled={resolvedApplyDisabled}
            onClick={onApply}
            customFontSize="text-sm leading-5"
            className="min-w-[100px]"
          >
            {i18n.apply}
          </Button>
        </footer>
      </div>
    </Drawer>
  );
};

export default memo(FilterSlideout);
